/**
 * Packages A–D and the combo. The letters are internal ids; visitors never see them.
 * Source: research note F.B/c workshop-pakker, 2026-10-10.
 * Prices stay off the site. Agendas, minute splits, headcount, and numeric
 * follow-ups in that note have no source, so they are not published.
 * The facilitator PDF is never a customer deliverable.
 */

export type PackageId = 'A' | 'B' | 'C' | 'D' | 'combo'
export type PackageSurface = 'landing' | 'agency' | 'hub'
export type SchemaType = 'Course' | 'Service'
export type CopyLang = 'nb' | 'en'

export interface ModuleLine {
  id: string
  name: string
  content: string
  note: string
}

export interface PackageCopy {
  id: PackageId
  anchor: string
  schemaType: SchemaType
  surfaces: readonly PackageSurface[]
  code: string
  name: string
  format: string
  audience: string
  summary: string
  points: readonly string[]
  modules: readonly ModuleLine[]
  notIncluded: string
  next: string
}

interface ModuleSource {
  id: string
  name: Record<CopyLang, string>
  content: Record<CopyLang, string>
}

interface ModuleRef {
  id: string
  note?: Record<CopyLang, string>
}

interface PackageSource {
  id: PackageId
  anchor: string
  schemaType: SchemaType
  surfaces: readonly PackageSurface[]
  code: string
  name: Record<CopyLang, string>
  format: Record<CopyLang, string>
  audience: Record<CopyLang, string>
  summary: Record<CopyLang, string>
  points: Record<CopyLang, readonly string[]>
  moduleRefs: readonly ModuleRef[]
  notIncluded: Record<CopyLang, string>
  next: Record<CopyLang, string>
}

const MODULES: readonly ModuleSource[] = [
  {
    id: 'M01',
    name: { nb: 'LLM-grunnlag', en: 'LLM basics' },
    content: {
      nb: 'Hva en språkmodell er, tokens og kontekst, styrker og svakheter, hallusinasjon, og grunnleggende prompting.',
      en: 'What a language model is, tokens and context, strengths and limits, hallucination, and a prompting foundation.',
    },
  },
  {
    id: 'M02',
    name: { nb: 'Fra chat til agenter som samarbeider', en: 'From chat to agents that work together' },
    content: {
      nb: 'Fra chat til en assistent med verktøy, videre til en agent, og videre til flere agenter. Live demo på F.B/c-plattformen.',
      en: 'From chat to an assistant with tools, then an agent, then several agents. A live demo on the F.B/c platform.',
    },
  },
  {
    id: 'M03',
    name: { nb: 'Ditt eget KI-team', en: 'Your own AI team' },
    content: {
      nb: 'Rolleoppdeling for agenter, instrukser og kunnskap, hvem som eier dem, og KPI-er med et nullpunkt.',
      en: 'How agent roles split, instructions and knowledge, who owns them, and KPIs with a baseline.',
    },
  },
  {
    id: 'M04',
    name: { nb: 'Sky eller lokale modeller', en: 'Cloud or local models' },
    content: {
      nb: 'API og sky mot lokalt eller on-prem, hvor dataene ligger, hva drift koster, og når hva passer.',
      en: 'API and cloud versus local or on-prem, where the data sits, what it costs to run, and when each fits.',
    },
  },
  {
    id: 'M05',
    name: { nb: 'Verktøy og abonnement', en: 'Tools and subscriptions' },
    content: {
      nb: 'ChatGPT, Claude, Gemini, Copilot og flere: hva abonnementene gir, og et valg som passer dere.',
      en: 'ChatGPT, Claude, Gemini, Copilot, and others: what the plans can do, and a choice that fits you.',
    },
  },
  {
    id: 'M06',
    name: { nb: 'Sikkerhet og GDPR', en: 'Security and GDPR' },
    content: {
      nb: 'Hva som kan deles, databehandleravtale, personvern, kompetansekrav i KI-forordningen, og et utkast til intern policy.',
      en: 'What can be shared, a data-processing agreement, privacy, the AI Act competence requirement, and a draft internal policy.',
    },
  },
  {
    id: 'M07',
    name: { nb: 'Implementering, testing og lansering', en: 'Implementation, testing, and launch' },
    content: {
      nb: 'Fra bruksområde til pilot, testsett og akseptkriterier, lansering, og deretter drift og måling.',
      en: 'From use case to pilot, a test set and acceptance criteria, launch, then operations and measurement.',
    },
  },
  {
    id: 'M08',
    name: { nb: 'Praktisk arbeid med egne oppgaver', en: 'Hands-on work on your own tasks' },
    content: {
      nb: 'Deltakerne jobber på egne oppgaver fra forsamtalen.',
      en: 'Participants work on real tasks gathered in the intro call.',
    },
  },
  {
    id: 'M09',
    name: { nb: 'Prioritering av bruksområder', en: 'Use-case prioritisation' },
    content: {
      nb: 'Gevinst, gjennomførbarhet og risiko i en matrise, og en kort liste til piloten.',
      en: 'A matrix of gain, feasibility, and risk, and a short list for the pilot.',
    },
  },
  {
    id: 'M10',
    name: { nb: 'Bygg i økten', en: 'Live build' },
    content: {
      nb: 'Bygg assistenter eller arbeidsflyter på deres egne verktøy, og test dem mot akseptkriterier.',
      en: 'Build assistants or workflows on your own tools, and test them against acceptance criteria.',
    },
  },
]

const SHORT: Record<CopyLang, string> = { nb: 'kort', en: 'short' }

function refs(...ids: string[]): ModuleRef[] {
  return ids.map(id => (id === 'M02' ? { id, note: SHORT } : { id }))
}

const PACKAGES: readonly PackageSource[] = [
  {
    id: 'A',
    anchor: 'a-ki-grunnmur',
    schemaType: 'Course',
    surfaces: ['landing', 'hub'],
    code: 'A',
    name: { nb: 'KI-grunnmur', en: 'AI foundation' },
    format: {
      nb: 'Halvdag, fysisk eller digitalt',
      en: 'Half day, in person or online',
    },
    audience: {
      nb: 'Team og ledergrupper i små og mellomstore bedrifter',
      en: 'Teams and leadership groups in small and medium-sized businesses',
    },
    summary: {
      nb: 'Halvdag, fysisk eller digitalt. For team og ledergrupper i små og mellomstore bedrifter som har prøvd ChatGPT eller Copilot litt, og som mangler en felles grunnmur.',
      en: 'Half day, in person or online. For teams and leadership groups in small and medium-sized businesses that have tried ChatGPT or Copilot a little and lack a shared foundation.',
    },
    points: {
      nb: ['Presentasjon tilpasset dere', 'Sammendrag med maler og promptbibliotek', 'Ett oppfølgingsmøte'],
      en: ['A presentation tailored to you', 'A summary with templates and a prompt library', 'One follow-up meeting'],
    },
    moduleRefs: refs('M01', 'M02', 'M05', 'M06', 'M08'),
    notIncluded: {
      nb: 'Ikke inkludert: KPI-nullpunkt, pilot, bygg, lisenser, reise utenfor Oslo-området, eller deltakere utover gruppen.',
      en: 'Not included: a KPI baseline, a pilot, building, licences, travel outside the Oslo area, or participants beyond the group.',
    },
    next: {
      nb: 'Neste steg kan være Fra verktøy til pilot, og deretter en implementeringssprint.',
      en: 'A next step can be From tools to pilot, and then an implementation sprint.',
    },
  },
  {
    id: 'B',
    anchor: 'b-fra-verktoy-til-pilot',
    schemaType: 'Course',
    surfaces: ['landing', 'hub'],
    code: 'B',
    name: { nb: 'Fra verktøy til pilot', en: 'From tools to pilot' },
    format: { nb: 'Heldag, fysisk', en: 'Full day, in person' },
    audience: { nb: 'Bedrifter som vil fra chat til første pilot', en: 'Companies moving from chat to a first pilot' },
    summary: {
      nb: 'Heldag, fysisk. For en bedrift som vil gå fra «vi bruker chat» til én konkret pilot. Dere går hjem med en pilotplan, ikke en ferdig pilot.',
      en: 'Full day, in person. For a company that wants to move from “we use chat” to one concrete pilot. You leave with a pilot plan, not a finished pilot.',
    },
    points: {
      nb: ['Presentasjon tilpasset dere', 'Sammendrag', 'Et KPI-nullpunkt', 'En pilotplan', 'Oppfølgingsmøter'],
      en: ['A presentation tailored to you', 'A summary', 'A KPI baseline', 'A pilot plan', 'Follow-up meetings'],
    },
    moduleRefs: [
      { id: 'M01' },
      { id: 'M02' },
      { id: 'M03' },
      { id: 'M04' },
      { id: 'M05' },
      { id: 'M06' },
      { id: 'M07' },
      { id: 'M09' },
      { id: 'M10' },
    ],
    notIncluded: {
      nb: 'Ikke inkludert: pilot i produksjon, integrasjoner mot fagsystem eller CRM, lisenser, eller arbeidsflyter utover økten.',
      en: 'Not included: a pilot in production, integrations to a line-of-business system or CRM, licences, or workflows beyond the session.',
    },
    next: {
      nb: 'Neste steg kan være en implementeringssprint, der piloten bygges på planen fra denne dagen, og deretter egen F.B/c-løsning.',
      en: 'A next step can be an implementation sprint, where the pilot is built on this day’s plan, and then your own F.B/c solution.',
    },
  },
  {
    id: 'C',
    anchor: 'c-implementeringssprint',
    schemaType: 'Service',
    surfaces: ['agency', 'hub'],
    code: 'C',
    name: { nb: 'Implementeringssprint', en: 'Implementation sprint' },
    format: {
      nb: 'Oppstartsdag, sprint og lanseringsmøte',
      en: 'Start day, sprint, and launch meeting',
    },
    audience: { nb: 'Et valgt bruksområde', en: 'A chosen use case' },
    summary: {
      nb: 'Oppstartsdag, sprint og lanseringsmøte. For en bedrift med et valgt bruksområde som vil ha én agent i drift. Implementering, ikke et kurs.',
      en: 'A start day, a sprint, and a launch meeting. For a company with a chosen use case that wants one agent in operation. Implementation, not a course.',
    },
    points: {
      nb: [
        'Presentasjon tilpasset dere',
        'Måling før og etter',
        'Én agent i drift på deres egne verktøy',
        'Et testsett og et driftsnotat',
        'Ett oppfølgingsmøte etter lansering',
      ],
      en: [
        'A presentation tailored to you',
        'A before-and-after measurement',
        'One agent in production on your own tools',
        'A test set and an operations note',
        'One follow-up meeting after launch',
      ],
    },
    moduleRefs: [{ id: 'M03' }, { id: 'M04' }, { id: 'M06' }, { id: 'M07' }, { id: 'M09' }, { id: 'M10' }],
    notIncluded: {
      nb: 'Ikke inkludert: en full egen versjon av F.B/c-plattformen, ny merkevareprofil, skriving tilbake til CRM, flere agenter, eller løpende drift etter oppfølgingen.',
      en: 'Not included: a full own version of the F.B/c platform, a brand overhaul, CRM write-back, more agents, or ongoing operations after the follow-up.',
    },
    next: {
      nb: 'Neste steg kan være egen F.B/c-løsning.',
      en: 'A next step can be your own F.B/c solution.',
    },
  },
  {
    id: 'D',
    anchor: 'd-opplaering-for-alle-ansatte',
    schemaType: 'Course',
    surfaces: ['landing', 'hub'],
    code: 'D',
    name: { nb: 'Opplæring for alle ansatte', en: 'Training for all employees' },
    format: {
      nb: 'Oppstart med alle, digitale runder og selvstudium',
      en: 'Kickoff with everyone, online rounds, and self-study',
    },
    audience: { nb: 'Alle ansatte', en: 'All employees' },
    summary: {
      nb: 'For bedrifter som trenger en felles grunnmur og dokumentert KI-kompetanse etter KI-forordningen. Oppstart med alle, digitale runder, selvstudium med opptak og sjekkliste, og en kursbevisliste.',
      en: 'For companies that need a shared foundation and documented AI competence under the AI Act. A kickoff with everyone, online rounds, self-study with a recording and a checklist, and a certificate list.',
    },
    points: {
      nb: [
        'Presentasjon tilpasset dere, og opptak',
        'Maler per rolle',
        'Deltakerliste og kursbevis som dokumentasjon etter KI-forordningen',
        'En enkel undersøkelse før og etter',
        'Ett oppfølgingsmøte med ledelsen',
      ],
      en: [
        'A presentation tailored to you, and a recording',
        'Templates per role',
        'A participant list and certificates as AI Act documentation',
        'A simple before-and-after survey',
        'One follow-up meeting with leadership',
      ],
    },
    moduleRefs: refs('M01', 'M02', 'M05', 'M06', 'M08'),
    notIncluded: {
      nb: 'Ikke inkludert: LMS-integrasjon, e-læringsplattform, individuell coaching, eller lisenser.',
      en: 'Not included: an LMS integration, an e-learning platform, individual coaching, or licences.',
    },
    next: {
      nb: 'Nøkkelpersoner kan gå videre til Fra verktøy til pilot, og deretter til en implementeringssprint eller egen F.B/c-løsning.',
      en: 'Key people can continue to From tools to pilot, and then to an implementation sprint or your own F.B/c solution.',
    },
  },
  {
    id: 'combo',
    anchor: 'egen-fbc-losning',
    schemaType: 'Service',
    surfaces: ['agency', 'hub'],
    code: 'Combo',
    name: { nb: 'Egen F.B/c-løsning', en: 'Your own F.B/c solution' },
    format: { nb: 'Egen plattform, oppstart og opplæring', en: 'Your own platform, kickoff, and training' },
    audience: { nb: 'Egen løsning og hele organisasjonen', en: 'Your own solution and the whole organisation' },
    summary: {
      nb: 'Deres egen versjon av F.B/c-plattformen med deres merkevare, tilpasset agent, booking og sjekkliste. Oppstarten skjer på deres versjon, og alle ansatte får opplæring.',
      en: 'Your own version of the F.B/c platform with your brand, an adapted agent, booking, and a checklist. The kickoff runs on your version, and all employees get training.',
    },
    points: {
      nb: [
        'Egen versjon med merkevare, tilpasset agent, booking og sjekkliste',
        'Oppstart på deres egen versjon',
        'Opplæring for alle ansatte i den nye løsningen',
      ],
      en: [
        'Your own version with your brand, an adapted agent, booking, and a checklist',
        'A kickoff on your own version',
        'Training for all employees on the new solution',
      ],
    },
    moduleRefs: [
      { id: 'M03' },
      { id: 'M04' },
      { id: 'M06' },
      { id: 'M07' },
      { id: 'M09' },
      { id: 'M10' },
      { id: 'M01' },
      { id: 'M02', note: SHORT },
      { id: 'M05' },
      { id: 'M08' },
    ],
    notIncluded: {
      nb: 'Ikke inkludert: flere kunder på samme plattform, skriving tilbake til CRM, eller kalendere som ikke støttes.',
      en: 'Not included: several customers on one platform, CRM write-back, or calendars that are not supported.',
    },
    next: { nb: '', en: '' },
  },
]

function moduleLine(ref: ModuleRef, lang: CopyLang): ModuleLine {
  const source = MODULES.find(item => item.id === ref.id)
  if (!source) throw new Error(`Unknown workshop module ${ref.id}`)
  return {
    id: source.id,
    name: source.name[lang],
    content: source.content[lang],
    note: ref.note?.[lang] ?? '',
  }
}

export function packageCopy(source: PackageSource, lang: CopyLang): PackageCopy {
  return {
    id: source.id,
    anchor: source.anchor,
    schemaType: source.schemaType,
    surfaces: source.surfaces,
    code: source.code,
    name: source.name[lang],
    format: source.format[lang],
    audience: source.audience[lang],
    summary: source.summary[lang],
    points: source.points[lang],
    modules: source.moduleRefs.map(ref => moduleLine(ref, lang)),
    notIncluded: source.notIncluded[lang],
    next: source.next[lang],
  }
}

export function packagesFor(surface: PackageSurface, lang: CopyLang): PackageCopy[] {
  return PACKAGES.filter(item => item.surfaces.includes(surface)).map(item => packageCopy(item, lang))
}

export function allPackages(lang: CopyLang): PackageCopy[] {
  return PACKAGES.map(item => packageCopy(item, lang))
}

export const COPY_LABEL = {
  modules: { nb: 'Moduler', en: 'Modules' },
  gets: { nb: 'Dere får', en: 'You get' },
} as const

export const WORKSHOPS_PATH = '/workshops'
export const AGENCY_PATH = '/ai-agency'
export const WORKSHOPS_URL = 'https://www.farzadbayat.com/workshops'
export const OG_IMAGE = 'https://www.farzadbayat.com/farzad-bayat.jpg'

export const PAGE_TITLE = {
  landing: {
    nb: 'KI-workshops for team | F.B/c Lab — Farzad Bayat',
    en: 'AI workshops for teams | F.B/c Lab — Farzad Bayat',
  },
  agency: {
    nb: 'KI-konsulent i Oslo | F.B/c Lab',
    en: 'AI consultant in Oslo | F.B/c Lab',
  },
  about: {
    nb: 'Om Farzad Bayat | F.B/c Lab — KI-konsulent og ingeniør',
    en: 'About Farzad Bayat | F.B/c Lab — AI consultant and engineer',
  },
  workshops: {
    nb: 'KI-workshops for team | F.B/c Lab',
    en: 'AI workshops for teams | F.B/c Lab',
  },
} as const satisfies Record<string, Record<CopyLang, string>>

/** Visible text on /workshops. The app and scripts/prerender-workshops.ts both read it. */
export const WORKSHOPS_PAGE = {
  nb: {
    title: 'KI-workshops',
    lead: 'Workshops og implementering for team som vil bruke KI i hverdagen.',
    helpQuestion: 'Vil dere ha hjelp til å bygge?',
    helpLink: 'Se KI-konsulent.',
    agencyLink: 'KI-konsulent',
    faqTitle: 'Spørsmål',
    navLabel: 'Workshops',
  },
  en: {
    title: 'AI workshops',
    lead: 'Workshops and implementation for teams who want to use AI day to day.',
    helpQuestion: 'Want help building it?',
    helpLink: 'See AI consulting.',
    agencyLink: 'AI consulting',
    faqTitle: 'Questions',
    navLabel: 'Workshops',
  },
} as const satisfies Record<CopyLang, Record<string, string>>

/** Visible text on the 404 page. */
export const NOT_FOUND_COPY = {
  nb: { title: 'Fant ikke siden', home: 'Til forsiden' },
  en: { title: 'Page not found', home: 'Back to home' },
} as const satisfies Record<CopyLang, Record<string, string>>

export const BOOKING_LABEL = {
  nb: 'Book en gratis 30-minutters samtale',
  en: 'Book a free 30-minute call',
} as const

export const CHAT_LABEL = {
  nb: 'Prat med F.B/c',
  en: 'Talk to F.B/c',
} as const

export interface WorkshopFaq {
  question: string
  answer: string
  /** Optional link shown at the start of the answer. */
  link?: { label: string; href: string }
}

const FAQ: Record<CopyLang, ReadonlyArray<WorkshopFaq>> = {
  nb: [
    {
      question: 'Hva koster en workshop?',
      answer: 'Prisen avhenger av omfang, og vi går gjennom den i en gratis samtale.',
    },
    {
      question: 'Hvordan booker jeg?',
      answer: 'Book en gratis 30-minutters samtale, eller ring +47 94 44 64 46.',
      link: { label: 'Book en gratis 30-minutters samtale', href: 'https://cal.com/farzad-bayat/30min' },
    },
  ],
  en: [
    {
      question: 'What does a workshop cost?',
      answer: 'It depends on scope, and we go through it on a free call.',
    },
    {
      question: 'How do I book?',
      answer: 'Book a free 30-minute call, or call +47 94 44 64 46.',
      link: { label: 'Book a free 30-minute call', href: 'https://cal.com/farzad-bayat/30min' },
    },
  ],
}

export function workshopFaqs(lang: CopyLang): ReadonlyArray<WorkshopFaq> {
  return FAQ[lang]
}

export function buildWorkshopsJsonLd(lang: CopyLang): Record<string, unknown> {
  const packages = allPackages(lang)
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'WebPage',
      '@id': `${WORKSHOPS_URL}#webpage`,
      url: WORKSHOPS_URL,
      name: PAGE_TITLE.workshops[lang],
      description: `${WORKSHOPS_PAGE[lang].lead} ${WORKSHOPS_PAGE[lang].helpQuestion}`,
      inLanguage: lang === 'nb' ? 'nb-NO' : 'en',
      isPartOf: { '@type': 'WebSite', name: 'F.B/c Lab', url: 'https://www.farzadbayat.com/' },
      primaryImageOfPage: OG_IMAGE,
    },
  ]

  for (const item of packages) {
    graph.push({
      '@type': item.schemaType,
      '@id': `${WORKSHOPS_URL}#${item.anchor}`,
      name: item.name,
      description: item.summary,
      url: `${WORKSHOPS_URL}#${item.anchor}`,
      provider: {
        '@type': 'Organization',
        name: 'F.B/c Lab',
        url: 'https://www.farzadbayat.com/',
      },
    })
  }

  graph.push({
    '@type': 'FAQPage',
    '@id': `${WORKSHOPS_URL}#faq`,
    mainEntity: workshopFaqs(lang).map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  })

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  }
}
